import type { ClickEvent } from '@athena-lib/types';
import { tick } from 'svelte';
import { generateUniqueElementId } from '@athena-lib/unique-element-id';
import {
  createKeyDownHandler,
  findOption,
  focusFirstOrSelected,
  focusNext,
  focusPrevious,
  select,
} from './utils';
import { useStore } from './store';
import type { ProductWizardDropdownProps, SelectedValue } from './types';

const store = (props?: ProductWizardDropdownProps) => {
  let isOpen = $state(false);
  let selected = $state<SelectedValue | undefined>();
  const ids = $derived.by(() => {
    const baseId = props?.id ?? generateUniqueElementId();

    return {
      trigger: baseId,
      menu: `${baseId}-listbox`,
    };
  });

  $effect(() => {
    if (props?.value && props.value !== selected?.value) {
      const menu = document.getElementById(ids.menu);
      selected = findOption(menu, props.value);
    }
  });

  const toggleOpen = (open: boolean, focusTrigger: boolean = true) => {
    isOpen = open;
    if (isOpen) {
      tick().then(() => {
        const listbox = document.getElementById(ids.menu);

        if (!listbox) {
          return;
        }

        focusFirstOrSelected(listbox);
      });
    } else if (!isOpen && focusTrigger) {
      tick().then(() => {
        const trigger = document.getElementById(ids.trigger);

        trigger?.focus();
      });
    }
  };

  const onChange = <T extends HTMLElement>(item: T) => {
    const { label, value, disabled } = item.dataset;

    if (disabled === 'true') {
      return;
    }

    if (!label || !value) {
      return;
    }

    selected = { label, value };
    props?.onChange?.(value);
    toggleOpen(false);
  };

  const handleClick = <T extends HTMLElement>(event: ClickEvent<T>) => {
    onChange(event.currentTarget);
  };

  const handleKeyDown = createKeyDownHandler({
    ArrowUp: focusPrevious,
    ArrowDown: focusNext,
    Space: select(onChange),
    Enter: select(onChange),
    Escape: () => toggleOpen(false),
    Tab: () => toggleOpen(false, false),
  });

  return {
    isOpen: () => isOpen,
    selected: () => selected,
    triggerId: () => ids.trigger,
    menuId: () => ids.menu,
    toggleOpen,
    handleKeyDown,
    handleClick,
  };
};

export const useDropdown = (props?: ProductWizardDropdownProps) =>
  useStore('dropdown', store, props);
